import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ChevronDown, Cookie, Facebook, Globe, Instagram, Linkedin, Menu, Twitter, X, Youtube } from "lucide-react";
import { configurarAnalitica, openCookieSettings, paginaVista } from "@/lib/consent";
import { LEGAL_LINKS } from "@/pages/Legal";
import { FUNCIONES, ISLAS, LANDINGS, RECURSOS, TODAS_FUNCIONES } from "./enlaces";
import { useDatosSeo } from "./datos";
import { useSinCanonica } from "./cabecera";
import { CompartirWhatsApp } from "./Compartir";

// Blog y Opiniones solo salen en el menú cuando tienen contenido publicado.
function useRecursos() {
  const d = useDatosSeo();
  return RECURSOS.filter((r) => (r.ruta !== "/blog" || d?.articulos?.length) && (r.ruta !== "/opiniones" || d?.resenas?.length));
}

export function useAnaliticaPublica(d) {
  const { pathname } = useLocation();
  const id = d?.ajustes?.ga4_id;
  useEffect(() => { if (!configurarAnalitica(id)) paginaVista(pathname); }, [id, pathname]);
}

function Desplegable({ titulo, items, id }) {
  return (
    <div className="group relative" data-testid={`web-menu-${id}`}>
      <button type="button" className="flex items-center gap-1 py-5 hover:text-white focus:text-white" aria-haspopup="true">
        {titulo} <ChevronDown className="h-4 w-4 transition-transform duration-200 group-hover:rotate-180 group-focus-within:rotate-180" />
      </button>
      <div className="invisible absolute left-1/2 top-full w-72 -translate-x-1/2 translate-y-1 rounded-2xl border border-white/10 bg-[#0A2540] p-2 opacity-0 shadow-2xl transition-[opacity,transform] duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
        {items.map((it) => (
          <Link key={it.ruta} to={it.ruta} className="block rounded-xl px-4 py-2.5 text-sm text-sky-100/90 transition-colors hover:bg-white/10 hover:text-white" data-testid={`web-menu-link-${it.ruta.slice(1)}`}>{it.nombre}</Link>
        ))}
      </div>
    </div>
  );
}

function MenuMovil() {
  const recursos = useRecursos();
  return (
    <nav className="border-t border-white/10 px-5 pb-6 pt-2 lg:hidden" aria-label="Menú" data-testid="web-menu-movil">
      {[["Funciones", [...FUNCIONES, TODAS_FUNCIONES]], ["Canarias", ISLAS], ["Recursos", recursos]].map(([t, items]) => (
        <div key={t} className="mt-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#00B4CC]">{t}</p>
          <div className="mt-2 grid grid-cols-2 gap-1">
            {items.map((it) => <Link key={it.ruta} to={it.ruta} className="rounded-lg px-2 py-2 text-sm text-sky-100/90 hover:bg-white/10" data-testid={`web-movil-${it.ruta.slice(1)}`}>{it.nombre}</Link>)}
          </div>
        </div>
      ))}
    </nav>
  );
}

function Cabecera() {
  const [abierto, setAbierto] = useState(false);
  const { pathname } = useLocation();
  const recursos = useRecursos();
  useEffect(() => { setAbierto(false); }, [pathname]);
  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0A2540]/95 backdrop-blur-xl" data-testid="web-cabecera">
      <div className="mx-auto flex h-16 max-w-6xl items-center gap-6 px-5">
        <Link to="/" className="flex items-center gap-2" data-testid="web-logo">
          <img src="/nuvlipro-logo.png" alt="NuvliPro" width="32" height="32" className="h-8 w-8 rounded-lg bg-white object-contain p-0.5" />
          <span className="font-heading text-lg font-extrabold text-white">Nuvli<span className="text-[#00B4CC]">Pro</span></span>
        </Link>
        <nav className="ml-auto hidden items-center gap-7 text-sm font-semibold text-sky-100/80 lg:flex" aria-label="Principal">
          <Desplegable titulo="Funciones" items={[...FUNCIONES, TODAS_FUNCIONES]} id="funciones" />
          <Desplegable titulo="Canarias" items={ISLAS} id="canarias" />
          {recursos.map((it) => <Link key={it.ruta} to={it.ruta} className="hover:text-white" data-testid={`web-nav-${it.ruta.slice(1)}`}>{it.nombre}</Link>)}
        </nav>
        <div className="ml-auto flex items-center gap-2 lg:ml-0">
          <Link to="/login" className="hidden rounded-full px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 sm:inline-flex" data-testid="web-entrar">Entrar</Link>
          <Link to="/login" className="inline-flex items-center gap-1 rounded-full bg-[#00B4CC] px-4 py-2 text-sm font-bold text-[#0A2540] transition-colors hover:bg-white" data-testid="web-empezar">Empieza gratis</Link>
          <button type="button" onClick={() => setAbierto((v) => !v)} className="rounded-lg p-2 text-white hover:bg-white/10 lg:hidden" aria-label="Abrir menú" aria-expanded={abierto} data-testid="web-menu-boton">
            {abierto ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {abierto && <MenuMovil />}
    </header>
  );
}

function Columna({ titulo, items }) {
  return (
    <div>
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#00B4CC]">{titulo}</p>
      <ul className="mt-4 space-y-2.5">
        {items.map((it) => <li key={it.ruta}><Link to={it.ruta} className="text-sm text-sky-100/75 transition-colors hover:text-white" data-testid={`web-pie-${it.ruta.slice(1)}`}>{it.nombre}</Link></li>)}
      </ul>
    </div>
  );
}

const REDES = [[/youtube\.com|youtu\.be/, "YouTube", Youtube], [/instagram\.com/, "Instagram", Instagram], [/facebook\.com/, "Facebook", Facebook],
  [/linkedin\.com/, "LinkedIn", Linkedin], [/twitter\.com|x\.com/, "X", Twitter]];
const redDe = (u) => REDES.find(([re]) => re.test(u)) || [null, "web", Globe];

// Redes oficiales guardadas en Administración SEO (las mismas del sameAs).
function Redes() {
  const redes = useDatosSeo()?.ajustes?.organizacion?.redes || [];
  if (!redes.length) return null;
  return (
    <ul className="mt-5 flex gap-2" data-testid="web-redes">
      {redes.map((u) => {
        const [, nombre, Icono] = redDe(u);
        return (
          <li key={u}>
            <a href={u} target="_blank" rel="noopener noreferrer me" aria-label={`NuvliPro en ${nombre}`} title={`NuvliPro en ${nombre}`}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-sky-100/80 transition-colors hover:border-[#00B4CC] hover:bg-[#00B4CC] hover:text-[#0A2540]"
              data-testid={`web-red-${nombre.toLowerCase()}`}><Icono className="h-4 w-4" /></a>
          </li>
        );
      })}
    </ul>
  );
}

// Enlaces a todas las páginas públicas (también se usan al pie de la portada).
export function MapaWeb() {
  const recursos = useRecursos();
  return (
    <div className="bg-[#061A2E] px-5 py-14" data-testid="web-mapa">
      <div className="mx-auto grid max-w-6xl gap-10 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <p className="font-heading text-lg font-extrabold text-white">Nuvli<span className="text-[#00B4CC]">Pro</span></p>
          <p className="mt-3 max-w-xs text-sm leading-relaxed text-sky-100/70">Software de gestión para autónomos y empresas de reformas y construcción.</p>
          <Link to="/login" className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-[#00B4CC] hover:text-white">Empieza gratis <ArrowRight className="h-4 w-4" /></Link>
          <Redes />
        </div>
        <Columna titulo="Funciones" items={[...FUNCIONES, ...LANDINGS.filter((l) => l.columna === "Funciones"), TODAS_FUNCIONES]} />
        <Columna titulo="Canarias" items={[...ISLAS, ...LANDINGS.filter((l) => l.columna === "Canarias")]} />
        <Columna titulo="Recursos" items={recursos} />
      </div>
    </div>
  );
}

function Pie() {
  return (
    <footer data-testid="web-pie">
      <MapaWeb />
      <div className="border-t border-white/10 bg-[#061A2E] px-5 py-6 text-xs text-sky-100/60">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-5 gap-y-2">
          <span>© {new Date().getFullYear()} NuvliPro</span>
          {LEGAL_LINKS.map(([s, label]) => <Link key={s} to={`/legal/${s}`} className="hover:text-white">{label}</Link>)}
          <button type="button" onClick={openCookieSettings} className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200" data-testid="web-configurar-cookies">
            <Cookie className="h-3.5 w-3.5" /> Configurar cookies
          </button>
        </div>
      </div>
    </footer>
  );
}

export function Cabezal({ etiqueta, titulo, texto, children }) {
  return (
    <section className="relative overflow-hidden bg-[#0A2540] text-white">
      <div className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:linear-gradient(#fff_1px,transparent_1px),linear-gradient(90deg,#fff_1px,transparent_1px)] [background-size:44px_44px]" aria-hidden="true" />
      <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#00B4CC]/25 blur-3xl" aria-hidden="true" />
      <div className="relative mx-auto max-w-6xl px-5 pb-16 pt-12 sm:pb-20 sm:pt-16">
        {children}
        <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-[#00B4CC]">{etiqueta}</p>
        <h1 className="font-heading mt-3 max-w-4xl text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl" data-testid="web-h1">{titulo}</h1>
        {texto && <p className="mt-6 max-w-2xl text-base leading-relaxed text-sky-100/80 sm:text-lg" data-testid="web-entradilla">{texto}</p>}
      </div>
    </section>
  );
}

export function Cargando() {
  return <div className="mx-auto max-w-6xl px-5 py-24"><div className="h-8 w-2/3 animate-pulse rounded-lg bg-slate-200" /><div className="mt-6 h-4 w-1/2 animate-pulse rounded bg-slate-200" /></div>;
}

export function NoEncontrada() {
  useSinCanonica();
  return (
    <div className="mx-auto max-w-3xl px-5 py-24 text-center" data-testid="web-no-encontrada">
      <p className="font-heading text-2xl font-bold text-[#0A2540]">Esta página no existe</p>
      <Link to="/" className="mt-4 inline-flex items-center gap-1 font-semibold text-[#008A9C]">Volver al inicio <ArrowRight className="h-4 w-4" /></Link>
    </div>
  );
}

export function PublicLayout({ d, children }) {
  useAnaliticaPublica(d);
  return (
    <div className="flex min-h-screen flex-col bg-[#F5F7FA] text-slate-700" data-testid="web-publica">
      <Cabecera />
      <main className="flex-1">{children}</main>
      <CompartirWhatsApp d={d} />
      <Pie />
    </div>
  );
}
